import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import '../styles/Categories.css';
import { API_KEY, SERVER_URL } from './../config';

const Categories = ({ onLogout }) => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editableCategory, setEditableCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    active: true,
  });

  // States for filtering
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/categories/`, {
        headers: {
          'X-API-Key': API_KEY,
        },
      });
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        console.error('Unexpected response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Memoize applyFilters using useCallback
  const applyFilters = useCallback(() => {
    const filtered = categories.filter((category) => {
      const activeMatch = (showActive && category.active) || (showInactive && !category.active);
      return activeMatch;
    });
    setFilteredCategories(filtered);
  }, [categories, showActive, showInactive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    switch (name) {
      case 'showActive':
        setShowActive(checked);
        break;
      case 'showInactive':
        setShowInactive(checked);
        break;
      default:
        break;
    }
  };

  const openModal = (category) => {
    setSelectedCategory(category);
    setEditableCategory({ ...category });
    setUpdateMessage('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedCategory(null);
    setEditableCategory(null);
    setUpdateMessage('');
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;
    if (type === 'checkbox') {
      newValue = e.target.checked;
    }
    setEditableCategory((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleUpdate = async () => {
    try {
      const response = await axios.patch(
        `${SERVER_URL}/categories/${selectedCategory.id}`,
        editableCategory,
        {
          headers: {
            'X-API-Key': API_KEY,
          },
        }
      );
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === selectedCategory.id ? response.data : category
        )
      );
      setUpdateMessage('Category updated successfully.');
    } catch (error) {
      console.error('Error updating category:', error);
      setUpdateMessage('Failed to update category. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${SERVER_URL}/categories/${selectedCategory.id}/`, {
        headers: {
          'X-API-Key': API_KEY,
        },
      });
      setCategories((prevCategories) =>
        prevCategories.filter((category) => category.id !== selectedCategory.id)
      );
      closeModal();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${SERVER_URL}/categories/`, newCategory, {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
        },
      });
      setCategories([...categories, response.data]);
      setShowCreateModal(false);
      setNewCategory({
        name: '',
        description: '',
        active: true,
      });
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleNewInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;
    if (type === 'checkbox') {
      newValue = e.target.checked;
    }
    setNewCategory((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  return (
    <div className="categories">
      <Navbar onLogout={onLogout} />
      <div className="content">
        <h1>Categories</h1>
        <button className="create-category-button" onClick={() => setShowCreateModal(true)}>
          Add Category
        </button>
        {/* Filter Options */}
        <div className="filter-options">
          <label>
            <input
              type="checkbox"
              name="showActive"
              checked={showActive}
              onChange={handleFilterChange}
            />
            Show Active
          </label>
          <label>
            <input
              type="checkbox"
              name="showInactive"
              checked={showInactive}
              onChange={handleFilterChange}
            />
            Show Inactive
          </label>
        </div>
        <div className="categories-container">
          <table className="categories-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((category, index) => (
                <tr
                  key={category.id}
                  className={index % 2 === 0 ? 'even' : 'odd'}
                  onClick={() => openModal(category)}
                >
                  <td>{category.name}</td>
                  <td>{category.description}</td>
                  <td>{category.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="categories-tiles">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="category-tile"
                onClick={() => openModal(category)}
              >
                <div className="category-name">{category.name}</div>
                <div className="category-description">
                  <label>Description: </label>
                  {category.description}
                </div>
                <div className="category-active">
                  <label>Active: </label>
                  {category.active ? 'Yes' : 'No'}
                </div>
              </div>
            ))}
          </div>
        </div>
        {isModalOpen && editableCategory && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Edit Category</h2>
              <form>
                <label>
                  Name:
                  <input
                    type="text"
                    name="name"
                    value={editableCategory.name}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Description:
                  <input
                    type="text"
                    name="description"
                    value={editableCategory.description}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={editableCategory.active}
                    onChange={handleInputChange}
                  />
                </label>
              </form>
              {updateMessage && (
                <div
                  className={`modal-message ${
                    updateMessage.includes('success') ? 'success' : 'error'
                  }`}
                >
                  {updateMessage}
                </div>
              )}
              <div className="modal-buttons">
                <button className="modal-button" onClick={handleUpdate}>
                  Update
                </button>
                <button className="modal-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
                <button className="modal-button close-button" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showCreateModal && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={() => setShowCreateModal(false)}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Create Category</h2>
              <form onSubmit={handleCreateCategory}>
                <label>
                  Name:
                  <input
                    type="text"
                    name="name"
                    value={newCategory.name}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Description:
                  <input
                    type="text"
                    name="description"
                    value={newCategory.description}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={newCategory.active}
                    onChange={handleNewInputChange}
                  />
                </label>
                <button type="submit">Create</button>
              </form>
              <button
                className="modal-button close-button"
                onClick={() => setShowCreateModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Categories;